import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth, esRolSeguridad, type SegUser } from '../stores/auth';

// Respuesta de /admin?action=login
type LoginResp = {
  access: string;
  refresh: string;
  admin: {
    id: number;
    rol: string;
    nombre?: string;
    apellido?: string;
    email?: string;
    municipio_id?: number | null;
    municipio_nombre?: string | null;
    destacamento_id?: number | null;
    destacamento_nombre?: string | null;
  };
};

export default function Login() {
  const [email, setEmail] = useState('');
  const [pass, setPass] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const nav = useNavigate();
  const setSession = useAuth((s) => s.setSession);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setErr(null);
    try {
      const data = await api<LoginResp>('/admin?action=login', {
        method: 'POST',
        body: JSON.stringify({ email, password: pass }),
        auth: false,
      });

      // Solo roles de seguridad (o admin/superadmin) pueden operar esta app.
      if (!esRolSeguridad(data.admin.rol)) {
        setErr('Tu cuenta no tiene acceso al módulo de Seguridad.');
        setLoading(false);
        return;
      }

      const user: SegUser = {
        id: data.admin.id,
        rol: data.admin.rol,
        nombre: data.admin.nombre ?? '',
        apellido: data.admin.apellido ?? '',
        email: data.admin.email ?? email,
        municipio_id: data.admin.municipio_id ?? null,
        municipio_nombre: data.admin.municipio_nombre ?? null,
        destacamento_id: data.admin.destacamento_id ?? null,
        destacamento_nombre: data.admin.destacamento_nombre ?? null,
      };
      setSession(data.access, data.refresh, user);
      nav('/', { replace: true });
    } catch (e: any) {
      setErr(e?.message || 'Error de login');
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="min-h-screen flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <img src={`${import.meta.env.BASE_URL}icon.png`} alt="" className="w-20 h-20 rounded-2xl mb-3 shadow-md bg-white" />
          <h1 className="text-2xl font-bold text-slate-900">Seguridad · SOS</h1>
          <p className="text-slate-500 text-sm">Central de despacho</p>
        </div>

        <form onSubmit={onSubmit} className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Email</label>
            <input
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-3 text-sm text-slate-900 focus:border-brand-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Contraseña</label>
            <input
              type="password"
              autoComplete="current-password"
              value={pass}
              onChange={(e) => setPass(e.target.value)}
              required
              className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-3 text-sm text-slate-900 focus:border-brand-500 outline-none"
            />
          </div>

          {err && (
            <div className="bg-red-50 border border-red-300 text-red-700 text-sm rounded-xl p-3">
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white rounded-xl py-3 font-semibold"
          >
            {loading ? 'Ingresando…' : 'Ingresar'}
          </button>
        </form>

        <p className="text-slate-400 text-xs text-center mt-6">
          Usá tu cuenta de personal de comisaría (jefe, agente o radio-operador).
        </p>
      </div>
    </main>
  );
}
